import { useInfiniteQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Bot, CircleAlert } from 'lucide-react';
import { api } from '../lib/api';
import { relativeTime } from '../lib/time';
import { Avatar, Spinner } from '@vatman/ui';
import './agentActivity.css';

/** One MCP tool call from the agent log (`agent_audit`). */
export interface AgentEntry {
  id: number;
  at: number;
  user: { id: string; name: string | null; color: string | null };
  token: { id: string | null; name: string };
  board: { id: string; title: string | null } | null;
  tool: string;
  args: Record<string, unknown>;
  items: number;
  ok: boolean;
  error: string | null;
  ms: number;
}

interface Page {
  entries: AgentEntry[];
  next: number | null;
}

/** Paged agent log from one of the log endpoints (`path` may carry filters). */
export function useAgentLog(path: string, enabled = true) {
  return useInfiniteQuery({
    queryKey: ['agent-log', path],
    enabled,
    initialPageParam: null as number | null,
    queryFn: ({ pageParam }) => api.get<Page & Record<string, unknown>>(`${path}${path.includes('?') ? '&' : '?'}limit=50${pageParam ? `&before=${pageParam}` : ''}`),
    getNextPageParam: (last) => last.next,
  });
}

/** Short one-line view of the logged arguments. */
function argsLine(args: Record<string, unknown>): string {
  const parts = Object.entries(args).map(([k, v]) => `${k}: ${typeof v === 'string' ? v : JSON.stringify(v)}`);
  return parts.join(' · ');
}

export function AgentLog({ path, showPerson, showBoard = true, enabled = true }: { path: string; showPerson?: boolean; showBoard?: boolean; enabled?: boolean }) {
  const { t, i18n } = useTranslation();
  const log = useAgentLog(path, enabled);
  const entries = log.data?.pages.flatMap((p) => p.entries) ?? [];
  const toolName = (tool: string) => (i18n.exists(`agents.tools.${tool}`) ? t(`agents.tools.${tool}`) : tool);
  if (log.isLoading) return <Spinner />;
  if (log.isError) return <div className="muted">{t('errors.internal')}</div>;
  if (!entries.length) return <div className="empty-state-panel">{t('agents.empty')}</div>;
  return (
    <div className="agent-log">
      {entries.map((e) => (
        <div key={e.id} className="agent-row" data-ok={e.ok}>
          {showPerson ? <Avatar name={e.user.name ?? '?'} color={e.user.color ?? '#888888'} size={26} /> : <Bot size={18} className="agent-row-icon" />}
          <div className="agent-row-main">
            <div className="agent-row-title">
              {showPerson && <b>{e.user.name}</b>}
              <span>{toolName(e.tool)}</span>
              {showBoard && e.board && (
                <a className="agent-row-board" href={`/b/${e.board.id}`}>
                  {e.board.title ?? e.board.id}
                </a>
              )}
              {e.items > 0 && <span className="badge">{t('agents.items', { count: e.items })}</span>}
            </div>
            {Object.keys(e.args).length > 0 && <div className="agent-row-args">{argsLine(e.args)}</div>}
            {!e.ok && (
              <div className="agent-row-error">
                <CircleAlert size={13} /> {e.error}
              </div>
            )}
            <div className="agent-row-meta">
              {relativeTime(e.at)} · {t('agents.token', { name: e.token.name })} · {e.ms} {t('agents.ms')}
            </div>
          </div>
        </div>
      ))}
      {log.hasNextPage && (
        <button className="btn sm" disabled={log.isFetchingNextPage} onClick={() => void log.fetchNextPage()}>
          {t('agents.more')}
        </button>
      )}
    </div>
  );
}
